/**
 * Centralized mutation hook for finalizing & signing an operative note.
 *
 * Architectural rules (per Principal QA audit):
 *  - No raw supabase.from(...).update() calls live in UI components.
 *  - PESSIMISTIC update only: UI must reflect "signed" state ONLY after the
 *    database confirms the write. No optimistic updates for clinical signatures.
 *  - On success, invalidates ['todays-or-case', caseId] so the case detail
 *    re-reads from the DB (auth log + signature timestamp included).
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useDemoMode } from '@/contexts/DemoModeContext';

export interface SignOpNoteInput {
  caseId: string;
  /** Optional: surgeon's attestation note appended to the audit trail. */
  attestationNote?: string;
}

export interface SignOpNoteResult {
  caseId: string;
  signedAt: string;
  /** True when the mutation ran in demo mode (no DB write). */
  demo: boolean;
}

export function useSignOpNote() {
  const queryClient = useQueryClient();
  const { isDemoMode } = useDemoMode();

  return useMutation<SignOpNoteResult, Error, SignOpNoteInput>({
    mutationFn: async ({ caseId, attestationNote }) => {
      const signedAt = new Date().toISOString();

      // Demo mode: skip DB write entirely. The UI will still pessimistically
      // wait for this promise to resolve before flipping to "signed".
      if (isDemoMode || !caseId || caseId.startsWith('demo-')) {
        return { caseId, signedAt, demo: true };
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // 1. Persist the signature on the authorization record. We also flip the
      //    status column to 'surgeon_approved' so the case shows up as signed in
      //    the Today's OR queue, which filters by status (not by the signature
      //    timestamp). Without this, the case stayed stuck in
      //    'awaiting_post_op_reconciliation' and disappeared from the dashboard.
      const { error: updErr } = await supabase
        .from('authorization_requests')
        .update({
          status: 'surgeon_approved',
          surgeon_attestation: true,
          surgeon_attestation_timestamp: signedAt,
          surgeon_approved_at: signedAt,
          updated_at: signedAt,
        })
        .eq('id', caseId);
      if (updErr) throw updErr;

      // 2. Append immutable audit trail entry. We let the DB-level audit log
      //    triggers compute the chain hash; we only INSERT (UPDATE/DELETE are
      //    blocked by restrictive RLS on audit_logs).
      await supabase.from('authorization_activity_log').insert([{
        authorization_id: caseId,
        actor_id: user.id,
        actor_type: 'surgeon',
        action: 'op_note_signed',
        details: {
          signed_at: signedAt,
          attestation_note: attestationNote ?? null,
        },
      }]);

      return { caseId, signedAt, demo: false };
    },
    onSuccess: (result) => {
      // Force a fresh read of the underlying case + reconciliation state.
      queryClient.invalidateQueries({ queryKey: ['todays-or-case', result.caseId] });
      queryClient.invalidateQueries({ queryKey: ['authorization-request', result.caseId] });
      queryClient.invalidateQueries({ queryKey: ['cpt-reconciliation', result.caseId] });
      // Refresh the Today's OR queue so the case visibly transitions to "Signed".
      queryClient.invalidateQueries({ queryKey: ['or-queue', 'authorized'] });
    },
  });
}
